import React, { createContext, useContext, useState } from "react";

export interface ModalContextType {
  isModal: boolean;
  setIsModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleModalToggle: () => void;
  show?: any;
  onHide?: any;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModalContext = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error(
      "useModalContext must be used within a ModalContextProvider"
    );
  }
  return context;
};

interface ModalContextProviderProps {
  children: React.ReactNode;
  show: any;
  onHide: () => void;
}

export const ModalContextProvider: React.FC<ModalContextProviderProps> = ({
  show,
  onHide,
  children,
}) => {
  const [isModal, setIsModal] = useState<boolean>(false);

  const handleModalToggle = () => setIsModal((value) => !value);

  /**
   * Stop the page scrolling behind an open modal.
   *
   * This was `classList.toggle("overflow-hidden")` in an effect keyed on
   * `[isModal, show, bodyElement]` — a toggle, not a set, so any re-render that
   * changed one of those flipped the lock the wrong way and left the body
   * unscrollable after the modal closed. Setting and restoring is the whole fix.
   */
  React.useEffect(() => {
    if (!show) return;

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [show]);

  /** Escape closes, as a dialog should. */
  React.useEffect(() => {
    if (!show || !onHide) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onHide();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [show, onHide]);

  return (
    <ModalContext.Provider
      value={{ isModal, setIsModal, handleModalToggle, show, onHide }}
    >
      {children}
    </ModalContext.Provider>
  );
};
